import React from 'react';
import { HtmlContent } from '@/components/ui/html-content';
import { toSpecialNameSlug } from '@/lib/reportAnalyticsSections';
import { cn } from '@/lib/utils';
import { Image } from '@/pages/reports/components/ui/Image';
import type { TooltipItem } from '@/pages/reports/components/form/TooltipListInput';
import type { ThematicSubsection } from '@/pages/reports/components/form/ThematicSubsectionsInput';
import { useReportI18n } from '@/pages/reports/i18n';

interface ThematicSessionCardProps {
  title: string;
  description?: string;
  validFrom?: string;
  validTo?: string;
  logoImage?: string;
  backgroundColor?: string;
  backgroundImage?: string;
  coverImage?: string;
  coverImage2?: string;
  coverImage3?: string;
  coverImage4?: string;
  titleLink?: string;
  subsections?: ThematicSubsection[];
  content?: string;
  className?: string;
}

function buildValidityText(
  t: (key: string, fallback?: string, vars?: Record<string, string | number>) => string,
  validFrom?: string,
  validTo?: string
): string | null {
  const from = (validFrom ?? '').trim();
  const to = (validTo ?? '').trim();
  if (!from && !to) return null;
  if (from && to) return `${from} – ${to}`;
  if (from) return t('thematic.from', undefined, { date: from });
  return t('thematic.until', undefined, { date: to });
}

function TooltipBalloon({ tooltip }: { tooltip: TooltipItem }) {
  const { t } = useReportI18n();
  const mode = tooltip.titleMode ?? 'ooooi';
  const bgColor = tooltip.backgroundColor?.trim() || (mode === 'como-usar' ? '#F5F5F5' : '#F2FF00');
  const textColor = tooltip.textColor?.trim() || '#000000';
  const wordBgColor = tooltip.titleWordBackgroundColor?.trim() || '#ffffff';
  const wordTextColor = tooltip.titleWordTextColor?.trim() || '#000000';

  const hasContent =
    (tooltip.content != null && tooltip.content.trim() !== '') ||
    (tooltip.items && tooltip.items.length > 0);

  const hasTitle =
    mode === 'como-usar' ||
    (mode === 'custom' && tooltip.titleCustom?.trim()) ||
    (mode === 'ooooi' && (
      (tooltip.ooooiPrefix ?? 'ooooi').trim() !== '' ||
      tooltip.titleWord?.trim()
    ));

  if (!hasTitle && !hasContent) return null;

  return (
    <div
      className="relative px-6 py-5 flex flex-col gap-4"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div
        className="absolute left-8 top-0 w-0 h-0 border-[10px] border-transparent border-t-0"
        style={{ transform: 'translateY(-100%)', borderBottomColor: bgColor }}
        aria-hidden
      />

      <div className="flex items-center flex-wrap gap-2 font-['Times_New_Roman'] font-normal text-[28px] leading-[100%]">
        {mode === 'como-usar' && <span>{t('thematic.howToUse')}</span>}
        {mode === 'custom' && tooltip.titleCustom && (
          <span>{tooltip.titleCustom} 👀</span>
        )}
        {(mode === 'ooooi' || !tooltip.titleMode) && (
          <>
            <span>{tooltip.ooooiPrefix ?? 'ooooi'} </span>
            {tooltip.titleWord ? (
              <span
                className="rounded px-1.5 py-0.5 align-middle"
                style={{ backgroundColor: wordBgColor, color: wordTextColor }}
              >
                {tooltip.titleWord}
              </span>
            ) : null}
            <span> 👀</span>
          </>
        )}
      </div>

      {hasContent && (
        <div className="text-sm md:text-base [&_*]:!text-inherit">
          {tooltip.content != null && tooltip.content.trim() !== '' ? (
            <HtmlContent
              html={tooltip.content}
              styleOptions={{ pDisplay: 'block', headingsDisplay: 'block' }}
            />
          ) : (
            (tooltip.items ?? []).map((item, i) => (
              <div key={i} className="mb-2 last:mb-0">
                <HtmlContent
                  html={item}
                  styleOptions={{ pDisplay: 'inline', headingsDisplay: 'block' }}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export const ThematicSessionCard: React.FC<ThematicSessionCardProps> = ({
  title,
  description,
  validFrom,
  validTo,
  logoImage,
  backgroundColor,
  backgroundImage,
  coverImage,
  coverImage2,
  coverImage3,
  coverImage4,
  titleLink,
  subsections = [],
  content,
  className,
}) => {
  const { t } = useReportI18n();
  const validityText = buildValidityText(t, validFrom, validTo);
  const fallbackSpecialName = toSpecialNameSlug(title);

  const images = [coverImage, coverImage2, coverImage3, coverImage4]
    .map((img) => (img ?? '').trim())
    .filter(Boolean) as string[];

  const sectionStyle: React.CSSProperties = {};
  if (backgroundColor?.trim()) sectionStyle.backgroundColor = backgroundColor.trim();
  if (backgroundImage?.trim()) {
    sectionStyle.backgroundImage = `url(${backgroundImage.trim()})`;
    sectionStyle.backgroundSize = 'cover';
    sectionStyle.backgroundPosition = 'center';
  }

  const contentBlocks =
    subsections.length > 0
      ? subsections.map((section, sectionIndex) => ({
          key: `subsection-${sectionIndex}`,
          specialName:
            toSpecialNameSlug(section.title) ||
            (fallbackSpecialName
              ? `${fallbackSpecialName}_${sectionIndex + 1}`
              : `especial_${sectionIndex + 1}`),
          node: (
            <>
              {section.title?.trim() && (
                <h3 className="font-normal text-[32px] leading-[36px] font-['Times_New_Roman']">
                  {section.title}
                </h3>
              )}
              {section.content && section.content.trim() !== '' && (
                <HtmlContent
                  html={section.content}
                  className="text-base md:text-lg text-gray-700 leading-relaxed"
                />
              )}
              {(section.tooltips ?? []).map((tooltip, tooltipIndex) => (
                <TooltipBalloon key={tooltipIndex} tooltip={tooltip} />
              ))}
            </>
          ),
        }))
      : content?.trim()
        ? [
            {
              key: 'legacy-content',
              specialName: fallbackSpecialName || 'especial_1',
              node: (
                <div className="relative px-6 py-5 flex flex-col gap-3 bg-[#F9F5E8] border-l-4 border-[#F2FF00]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                    {t('thematic.associatedContent')}
                  </p>
                  <HtmlContent
                    html={content}
                    className="text-sm md:text-base text-gray-800 leading-relaxed"
                  />
                </div>
              ),
            },
          ]
        : [];

  return (
    <section className={cn('w-full', className)} style={sectionStyle}>
      <div className="container mx-auto">
        <div className="max-w-[500px] mx-auto py-8 md:py-10 flex flex-col gap-6">

          <header className="flex items-center justify-between flex-wrap gap-2">
            <span className="px-3 py-1 rounded-full bg-[#F2FF00] text-xs font-semibold uppercase tracking-wide">
              {t('thematic.badge')}
            </span>
            {validityText && (
              <span className="text-xs font-medium text-gray-600">{validityText}</span>
            )}
          </header>

          {logoImage?.trim() && (
            <div className="flex justify-center">
              <Image
                src={logoImage.trim()}
                alt=""
                className="h-auto w-auto max-w-full"
              />
            </div>
          )}

          {images.length > 0 && (
            <div className="w-full h-auto">
              {images.length === 1 ? (
                <div className="flex justify-center">
                  <Image src={images[0]} alt="" className="h-auto w-auto max-w-full" />
                </div>
              ) : images.length === 2 ? (
                <div className="grid grid-cols-2 w-full gap-0">
                  {images.map((img, i) => (
                    <div key={i} className="flex justify-center items-center">
                      <Image src={img} alt="" className="h-auto w-auto max-w-full" />
                    </div>
                  ))}
                </div>
              ) : images.length === 3 ? (
                <div className="grid grid-cols-2 w-full gap-0">
                  <div className="row-span-2 flex justify-center items-center">
                    <Image src={images[0]} alt="" className="h-auto w-auto max-w-full" />
                  </div>
                  <div className="flex justify-center items-center">
                    <Image src={images[1]} alt="" className="h-auto w-auto max-w-full" />
                  </div>
                  <div className="flex justify-center items-center">
                    <Image src={images[2]} alt="" className="h-auto w-auto max-w-full" />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 w-full gap-0">
                  {images.map((img, i) => (
                    <div key={i} className="flex justify-center items-center">
                      <Image src={img} alt="" className="h-auto w-auto max-w-full" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div
            data-report-section="especial-title"
            data-report-section-name="especial_content"
            data-report-link-event="especial_content_link_clicked"
            data-report-special-name={fallbackSpecialName}
          >
            <h2 className="font-normal text-[42px] leading-[46px] font-['Times_New_Roman']">
              {titleLink?.trim() ? (
                <a href={titleLink.trim()} target="_blank" rel="noopener noreferrer" className="underline">
                  {title}
                </a>
              ) : (
                title
              )}
            </h2>

            {description?.trim() && (
              <HtmlContent
                html={description}
                className="text-base md:text-lg text-gray-700 leading-relaxed mt-6"
              />
            )}
          </div>

          {contentBlocks.map((block) => (
            <div
              key={block.key}
              className="flex flex-col gap-6"
              data-report-section={block.key}
              data-report-section-name="especial_content"
              data-report-link-event="especial_content_link_clicked"
              data-report-special-name={block.specialName}
            >
              {block.node}
            </div>
          ))}

        </div>
      </div>
    </section>
  );
};
